// JS
import React, { useState } from 'react';
import {
  randomId,
  getPreviousBreakpoint,
  useTailwindMediaQuery
} from '../shared/utils';

import { Swiper, SwiperSlide } from 'swiper/react/swiper-react';

import {
  Navigation,
  Pagination,
  Scrollbar,
  Grid,
  EffectFade,
  Autoplay,
  Mousewheel,
  FreeMode,
  Thumbs,
  Zoom
} from 'swiper';

// Import Swiper styles
import 'swiper/swiper.min.css';
import 'swiper/modules/navigation/navigation.min.css';
import 'swiper/modules/pagination/pagination.min.css';
import 'swiper/modules/scrollbar/scrollbar.min.css';
import 'swiper/modules/grid/grid.min.css';
import 'swiper/modules/effect-fade/effect-fade.min.css';
import 'swiper/modules/zoom/zoom.min.css';

import { chevronUp, chevronDown } from '../shared/svgData';

const Slider = ({
  name = null,
  responsive = null,
  infinite = false,
  matchHeight = true,
  useNavigation = false,
  usePagination = false,
  usePaginationDynamic = false,
  useScrollbar = false,
  useGrid = false,
  useFade = false,
  useAutoplay = false,
  useMousewheel = false,
  useFreemode = false,
  useThumbs = false,
  useZoom = false,
  paginationOptions = {},
  scrollbarOptions = {},
  children,
  paginationOptionsMobGallery,
  paginationAltBgColor = false,
  useNavigationDesktop = false,
  useGallerySlider = false,
  useGallerySliderPopup = false,
  usePopupThumbs = false,
  showPopupThumbs = true,
  showGalleryPopup,
  setSwiperIndex,
  tooltipText
}) => {
  const uniqueId = randomId();
  const isMobile = useTailwindMediaQuery('lg');
  const [imagesNavSlider, setImagesNavSlider] = useState(null);

  const upArrow = (
    <a
      data-previous-arrow={uniqueId}
      className="absolute inset-x-0 top-10 w-full h-auto z-10 text-heroarrows flex flex-col items-center justify-center px-28 text-arrow cursor-pointer opacity-30 hover:opacity-100 transition-opacity ease-in duration-250"
    >
      <span className="w-20 h-20">{chevronUp}</span>
    </a>
  );

  const downArrow = (
    <a
      data-next-arrow={uniqueId}
      className="absolute inset-x-0 bottom-10 w-full h-auto z-10 text-heroarrows flex flex-col items-center justify-center px-28 text-arrow cursor-pointer opacity-30 hover:opacity-100 transition-opacity ease-in duration-250"
    >
      <span className="w-20 h-20">{chevronDown}</span>
    </a>
  );

  const navigation = {
    prevEl: `[data-previous-arrow="${uniqueId}"]`,
    nextEl: `[data-next-arrow="${uniqueId}"]`
  };

  const pagination = {
    el: `[data-pagination="${uniqueId}"]`,
    clickable: true,
    renderBullet: function (index, className) {
      if (paginationOptions.rounded) {
        return `<span class="block relative my-3 mr-1.5 w-4 h-4 max-w-[6rem] bg-transparent cursor-pointer after:absolute after:max-w-[6rem] after:w-4 after:h-4 after:top-50 after:rounded-full ${paginationOptionsMobGallery} ${className}"></span>`;
      } else {
        return `<span class="block relative py-6 mx-3 first:ml-0 last:mr-0 w-full h-1 max-w-[6rem] bg-transparent cursor-pointer after:absolute after:max-w-[6rem] after:w-full after:h-1 after:top-50 ${className}"></span>`;
      }
    },
    bulletClass: paginationAltBgColor
      ? 'after:bg-gray-300'
      : 'after:bg-gray-200',
    bulletActiveClass: 'after:bg-gray-500'
  };

  const paginationDynamic = {
    el: `[data-pagination="${uniqueId}"]`,
    clickable: true,
    dynamicBullets: true
  };

  const modules = [];
  if (useNavigation) modules.push(Navigation);
  if (useNavigationDesktop) modules.push(Navigation);
  if (usePagination) modules.push(Pagination);
  if (useScrollbar) modules.push(Scrollbar);
  if (useGrid) modules.push(Grid);
  if (useFade) modules.push(EffectFade);
  if (useAutoplay) modules.push(Autoplay);
  if (useMousewheel) modules.push(Mousewheel);
  if (useFreemode) modules.push(FreeMode);
  if (useThumbs) modules.push(Thumbs);
  if (useZoom) modules.push(Zoom);

  let breakpoints = {};
  if (responsive != null) {
    var lastObj = {};
    for (var key in responsive) {
      var twBreakpoint = getPreviousBreakpoint(key);
      var item = responsive[key];
      var obj = JSON.parse(JSON.stringify(lastObj));
      for (var itemKey in item) {
        if (itemKey === 'show') obj.slidesPerView = item[itemKey];
        else obj[itemKey] = item[itemKey];
      }
      Object.assign(breakpoints, { [twBreakpoint]: obj });
      lastObj = obj;
    }
  }

  /* Tooltip Hover - Desktop Popup Gallery
  const tooltip = document.querySelectorAll('#tooltip');
  document.addEventListener('mousemove', fn, false);
  function fn(e) {
    for (var i = tooltip.length; i--; ) {
      var x = e.clientX,
        y = e.clientY;
      tooltip[i].style.top = y + 'px';
      tooltip[i].style.left = x + 'px';
    }
  }
  */

  return (
    <div
      id={(name != null ? name : 'swiper') + '-' + uniqueId}
      className={`clearfix ${useGallerySlider ? 'slider-gallery-wrapper' : ''}`}
    >
      <div
        className={
          useGallerySlider
            ? useGallerySliderPopup
              ? 'slider-gallery-grid-popup'
              : 'slider-gallery-grid'
            : 'slider-normal'
        }
      >
        {useGallerySlider && showPopupThumbs && (
          <div
            className={
              useGallerySliderPopup
                ? 'slider-gallery-column-popup'
                : 'slider-gallery-column'
            }
          >
            <div className="slider-gallery-prev" />
            <div
              className={`slider-gallery-thumb ${
                usePopupThumbs ? 'slider-gallery-thumb-popup' : ''
              }`}
            >
              <Swiper
                onSwiper={setImagesNavSlider}
                modules={modules}
                direction="vertical"
                spaceBetween={10}
                slidesPerView={
                  usePopupThumbs
                    ? 3
                    : document.querySelectorAll(
                        '.slider-gallery-thumb .swiper-slide'
                      ).length < 5
                    ? document.querySelectorAll(
                        '.slider-gallery-thumb .swiper-slide'
                      ).length
                    : 5
                }
                navigation={{
                  nextEl: '.slider-gallery-next',
                  prevEl: '.slider-gallery-prev'
                }}
                observer={true}
                observeParents={true}
                watchSlidesProgress={true}
                className="slider-gallery-container-thumb"
              >
                {children != null &&
                  children.map((child, index) => {
                    return (
                      <SwiperSlide
                        key={uniqueId + index}
                        className={
                          'p-0 cursor-pointer active:cursor-grabbing' +
                          (matchHeight ? ' h-auto' : '') +
                          (' thumb-number-' + index)
                        }
                      >
                        {child}
                      </SwiperSlide>
                    );
                  })}
              </Swiper>
            </div>
            <div className="slider-gallery-next" />
          </div>
        )}

        <div
          className={
            useGallerySlider
              ? useGallerySliderPopup
                ? 'slider-gallery-main-image-popup'
                : 'slider-gallery-main-image'
              : 'slider-normal-main-image'
          }
          onClick={showGalleryPopup}
        >
          {useGallerySlider && useGallerySliderPopup && !isMobile && (
            <span id="tooltip">{tooltipText}</span>
          )}
          {useNavigation && isMobile && upArrow}
          <Swiper
            onSwiper={setSwiperIndex}
            modules={modules}
            breakpoints={breakpoints}
            loop={infinite}
            loopedSlides={children && children.length}
            pagination={
              usePagination && usePaginationDynamic
                ? paginationDynamic
                : pagination
            }
            navigation={
              useNavigationDesktop
                ? {
                    nextEl: '.slider-gallery-next',
                    prevEl: '.slider-gallery-prev'
                  }
                : useNavigation && navigation
            }
            direction={useGallerySlider ? 'vertical' : 'horizontal'}
            thumbs={useThumbs && { swiper: imagesNavSlider }}
            mousewheel={
              useMousewheel && {
                forceToAxis: true,
                sensitivity: 1,
                releaseOnEdges: false
              }
            }
            freeMode={useFreemode}
            grid={
              useGrid &&
              !isMobile && {
                fill: 'row',
                rows: 2
              }
            }
            autoplay={
              useAutoplay && {
                delay: 6000,
                disableOnInteraction: false
              }
            }
            effect={useFade && 'fade'}
            scrollbar={{
              el: `[data-scrollbar="${uniqueId}"]`,
              draggable: true,
              hide: true
            }}
            zoom={
              useZoom && {
                maxRatio: 2,
                toggle: true
              }
            }
            observer={true}
            observeParents={true}
            className={
              (useGallerySlider && 'slider-gallery-container-main ') +
              'relative'
            }
          >
            {children != null &&
              children.map((child, index) => {
                return (
                  <SwiperSlide
                    key={uniqueId + index}
                    className={
                      'p-0 cursor-pointer active:cursor-grabbing' +
                      (matchHeight ? ' h-auto' : '') +
                      (' image-number-' + index)
                    }
                  >
                    {useZoom ? (
                      <div className="swiper-zoom-container">{child}</div>
                    ) : (
                      <>{child}</>
                    )}
                  </SwiperSlide>
                );
              })}

            {usePagination && (
              <div
                data-pagination={uniqueId}
                className={
                  (paginationOptions.noContain ? '' : 'container ') +
                  'flex justify-center items-center mt-8 md:mt-12 ' +
                  (paginationOptions?.classes?.wrapper
                    ? ' ' + paginationOptions.classes.wrapper
                    : '')
                }
              ></div>
            )}
          </Swiper>
          {useNavigation && isMobile && downArrow}
        </div>
      </div>

      {useScrollbar && (
        <div className="container">
          <div
            data-scrollbar={uniqueId}
            className={
              'h-[4px] bg-gray-200 mt-2 md:mt-4 rounded-full mx-auto cursor-pointer active:cursor-grabbing' +
              (scrollbarOptions?.classes ? ' ' + scrollbarOptions.classes : '')
            }
          ></div>
        </div>
      )}
    </div>
  );
};

export default Slider;
